// creativeIntel — orchestrates the two creative-analysis actions on The Web,
// both run inline from the creative (no dependency on any other page):
//
//   • generateBrief → analyze the video, its drop-off curve, and rewrite the
//                     script to fix where viewers leave, then bank the
//                     learnings in the knowledge base.
//   • runBrainScan  → extract the script + key frames and run the Tribe v2
//                     neural scan, then bank the learnings in the KB.
//
// Both reuse AIService for model work, creativeMedia for media handling, and
// webKnowledge to grow the KB.

import {
  resolveCreativeMedia,
  fetchMediaAsFile,
  extractKeyFrames,
  nodeIsVideo,
} from "./creativeMedia";
import { ingestCreativeLearning } from "./webKnowledge";

const pctText = (v) => `${Math.round((Number(v) || 0) * 100)}%`;

/**
 * Render a variation brief (structured object from AIService.generateVariationBrief)
 * into copy-ready markdown.
 */
export const buildBriefMarkdown = ({ node = {}, brief = {}, retention = null }) => {
  const lines = [`# Variation brief — ${node.label || "Untitled"}`];
  const id = node?._meta?.ad_id || node?._meta?.creative_id || node?.id;
  if (id) lines.push(`Source creative: ${id}`);
  lines.push(
    `Format: ${(node.format || "unknown").toString().replace(/_/g, " ")} · Funnel: ${
      node.funnel ? String(node.funnel).toUpperCase() : "untagged"
    } · Angle: ${node.angle || "untagged"}`,
  );

  lines.push("", "## Performance baseline");
  lines.push(`Spend: $${(node.spend || 0).toLocaleString()}`);
  lines.push(`ROAS: ${(node.roas || 0).toFixed(2)}`);
  lines.push(`CTR: ${(node.ctr || 0).toFixed(2)}%`);
  if (node.hookRate != null) lines.push(`Hook rate: ${pctText(node.hookRate)}`);

  if (retention) {
    lines.push("", "## Watch-through / drop-off");
    lines.push(
      "Retention: " +
        (retention.curve || []).map((p) => `${p.label} ${pctText(p.pct)}`).join(" → "),
    );
    if (retention.dropOff) {
      lines.push(
        `Sharpest drop: ${retention.dropOff.from} → ${retention.dropOff.to} (−${pctText(
          retention.dropOff.dropPct,
        )})`,
      );
    }
  }

  if (brief.thesis) lines.push("", "## The single idea", brief.thesis);

  if (brief.dropOffDiagnosis) {
    lines.push("", "## Why viewers dropped", brief.dropOffDiagnosis);
  }
  if (brief.dropPoints?.length) {
    brief.dropPoints.forEach((d) => {
      lines.push(`- **${d.at || "?"}** — ${d.reason}${d.fix ? ` → Fix: ${d.fix}` : ""}`);
    });
  }

  if (brief.whatWorked?.length) {
    lines.push("", "## Keep (what worked)");
    brief.whatWorked.forEach((w) => lines.push(`- ${w}`));
  }

  if (brief.scriptAdjustments?.length) {
    lines.push("", "## Script adjustments");
    brief.scriptAdjustments.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
  }

  if (brief.adjustedScript) {
    lines.push("", "## Adjusted script", brief.adjustedScript);
  }

  if (brief.hookOptions?.length) {
    lines.push("", "## Hook options");
    brief.hookOptions.forEach((h, i) => lines.push(`${i + 1}. ${h}`));
  }

  if (brief.bannedElements?.length) {
    lines.push("", "## Banned elements");
    brief.bannedElements.forEach((b) => lines.push(`- ${b}`));
  }

  if (brief.citations?.length) {
    lines.push("", "## Sources");
    brief.citations.forEach((c) =>
      lines.push(`- ${c.source}${c.locator ? ` (${c.locator})` : ""}${c.claim ? ` — ${c.claim}` : ""}`),
    );
  }

  return lines.join("\n");
};

/**
 * generateBrief — the full "Generate variation brief" pipeline.
 *
 * @returns { brief, script, retention, markdown, media }
 */
export const generateBrief = async ({
  node,
  aiService,
  workspaceId,
  model,
  onProgress,
  signal,
} = {}) => {
  const isVideo = nodeIsVideo(node);
  const retention = node?.retention || null;

  onProgress?.("Resolving creative…");
  const media = await resolveCreativeMedia(node);

  // Try to get the bytes so we can transcribe. CORS may block remote media —
  // in that case we fall back to any cached transcript on the node.
  let script = null;
  onProgress?.(isVideo ? "Extracting script from video…" : "Reading on-screen copy…");
  const sourceUrl = media.videoUrl || media.imageUrl;
  const file = await fetchMediaAsFile(sourceUrl, node?.label || "creative");
  if (file) {
    try {
      script = await aiService.extractScript(file, {
        isVideo: media.kind === "video",
        signal,
      });
    } catch (error) {
      if (signal?.aborted) throw error;
      script = null;
    }
  }
  if ((!script || !script.transcript) && node?.transcript) {
    script = script || { transcript: "", onScreenText: [], hook: "", scenes: [] };
    script.transcript = script.transcript || node.transcript;
  }

  onProgress?.("Analyzing drop-off + rewriting script…");
  const brief = await aiService.generateVariationBrief({
    node,
    script,
    retention,
    model,
    signal,
  });

  const markdown = buildBriefMarkdown({ node, brief, retention });

  // Bank the learning so the KB compounds.
  onProgress?.("Saving learnings to knowledge base…");
  await ingestCreativeLearning({
    workspaceId,
    node,
    kind: "brief",
    learning: {
      retention,
      transcript: script?.transcript || node?.transcript || "",
      whatWorked: brief.whatWorked,
      dropOffDiagnosis: brief.dropOffDiagnosis,
      scriptAdjustments: brief.scriptAdjustments,
      thesis: brief.thesis,
    },
  }).catch(() => {});

  return { brief, script, retention, markdown, media };
};

/**
 * runBrainScan — the full "Send to Brain" pipeline, run inline (no page):
 *   1. resolve the creative's media (video source or thumbnail),
 *   2. extract the script (voiceover + on-screen text + beats),
 *   3. sample key frames,
 *   4. run the Tribe v2 neural scan + AI interpretation,
 *   5. bank the learnings in the knowledge base.
 *
 * Steps degrade independently: if the Tribe server isn't configured the script
 * + frames still return; if the media bytes can't be fetched (CORS) the scan
 * falls back to the resolved URL and the script to any cached transcript.
 *
 * @returns { media, script, frames, tribe, interpretation }
 */
export const runBrainScan = async ({
  node,
  aiService,
  workspaceId,
  onProgress,
  signal,
} = {}) => {
  onProgress?.("Resolving creative…");
  const media = await resolveCreativeMedia(node);
  const isVideo = media.kind === "video";
  const sourceUrl = media.videoUrl || media.imageUrl;
  const file = await fetchMediaAsFile(sourceUrl, node?.label || "creative");

  onProgress?.("Extracting script, frames, and neural response…");

  const scriptTask = (async () => {
    if (file) {
      try {
        return await aiService.extractScript(file, { isVideo, signal });
      } catch (error) {
        if (signal?.aborted) throw error;
      }
    }
    if (node?.transcript) {
      return { transcript: node.transcript, onScreenText: [], hook: "", scenes: [] };
    }
    return null;
  })();

  const framesTask = extractKeyFrames({
    aiService,
    source: file || media.videoUrl,
    kind: media.kind,
    imageUrl: media.imageUrl,
    count: 5,
    signal,
  }).catch(() => []);

  const tribeTask = (async () => {
    try {
      const context = file
        ? { file, type: isVideo ? "video" : "image" }
        : { videoUrl: media.videoUrl, imageUrl: media.imageUrl, type: isVideo ? "video" : "image" };
      const tribe = await aiService.runTribeV2Analysis(context, {
        signal,
        mediaType: isVideo ? "video" : "image",
      });
      if (tribe?.error) return { tribe, interpretation: null };
      let interpretation = null;
      try {
        interpretation = await aiService.interpretBrainScan(tribe, { signal, isVideo });
      } catch (error) {
        /* interpretation is best-effort */
      }
      return { tribe, interpretation };
    } catch (error) {
      if (signal?.aborted) throw error;
      return { tribe: { error: error?.message || String(error) }, interpretation: null };
    }
  })();

  const [script, frames, tribeOut] = await Promise.all([scriptTask, framesTask, tribeTask]);
  const { tribe, interpretation } = tribeOut;

  onProgress?.("Saving learnings to knowledge base…");
  await ingestCreativeLearning({
    workspaceId,
    node,
    kind: "brain",
    learning: {
      retention: node?.retention || null,
      transcript: script?.transcript || node?.transcript || "",
      brainInsight: interpretation?.overall || interpretation?.advertiserInsights || "",
      thesis: interpretation?.consumerExperience || "",
    },
  }).catch(() => {});

  return { media, script, frames, tribe, interpretation };
};

export { extractKeyFrames, ingestCreativeLearning };
